import React, { useEffect, useRef, useState } from 'react';
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Option } from "modules/forms/hosted/formElements/Combobox";
import useFormFlow from "modules/forms/hooks/useFormFlow";
import cn from "classnames";

type Props = {
    name: string;
    id: any;
    label?: string;
    placeholder?: string;
    options: Option[];
}

export default function Select(props: Props) {
    const { values, onChange } = useFormFlow();
    const { name, label, placeholder } = props;
    const buttonRef = useRef(null);
    const options = Array.isArray(props.options) ? props.options : [];
    const selectedOption = options.find((o) => o.value == values[name]);
    const [hasNotEnoughSpaceBelow, setNotHasEnoughSpaceBelow] = useState(false);

    useEffect(() => {
        if (buttonRef.current) {
            const spaceBelow = window.innerHeight - buttonRef.current.getBoundingClientRect().bottom;
            setNotHasEnoughSpaceBelow(spaceBelow < 600);
        }
    }, []);

    const listBoxOptionCn = cn(
        "absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm",
        hasNotEnoughSpaceBelow && "bottom-0"
    );

    return (
        <Listbox value={selectedOption ?? null} onChange={option => onChange(name, option?.value)}>
            <Label
                className="block text-sm/6 font-medium text-textColor"
                dangerouslySetInnerHTML={{
                    __html: label,
                }}
            />
            <div className="relative mt-2">
                <ListboxButton
                    ref={buttonRef}
                    className="grid h-11 w-full cursor-default grid-cols-1 rounded-md bg-gray-50 py-2.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                >
          <span className="col-start-1 row-start-1 flex items-center gap-3 pr-6">
            {/*<img alt="" src={selected.avatar} className="size-5 shrink-0 rounded-full"/>*/}
              {selectedOption ? (
                  <span className="block truncate">{selectedOption?.label}</span>
              ) : (
                  <span className="block truncate text-gray-500">{placeholder ?? ""}</span>
              )}

          </span>
                    <ChevronUpDownIcon
                        aria-hidden="true"
                        className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                    />
                </ListboxButton>

                <ListboxOptions transition className={listBoxOptionCn}>
                    {options.map(option => (
                        <ListboxOption
                            key={option.value}
                            value={option}
                            className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-indigo-600 data-[focus]:text-white data-[focus]:outline-none"
                        >
                            <div className="flex items-center">
                                {/*<img alt="" src={option.avatar} className="size-5 shrink-0 rounded-full"/>*/}
                                <span className="ml-3 block truncate font-normal group-data-[selected]:font-semibold">
                                    {option.label}
                                </span>
                            </div>

                            <span
                                className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-selected])]:hidden group-data-[focus]:text-white"
                            >
                                <CheckIcon aria-hidden="true" className="size-5"/>
                            </span>
                        </ListboxOption>
                    ))}
                </ListboxOptions>
            </div>
        </Listbox>
    );
}
